import { DestroyRef, Injectable, NgZone, inject, signal } from '@angular/core';

import { AppointmentRecording } from '../interfaces/academic-appointment';
import { InspectableSession } from '../interfaces/inspection';

/**
 * Ce qu'on peut faire d'une séance à inspecter, et quand.
 *
 * <p>Une horloge à la minute : « terminée » ne doit pas être figé à l'heure du
 * rendu, une séance qui s'achève pendant qu'on travaille bascule d'elle-même
 * de « rejoindre » vers « consulter l'enregistrement ». Un seul service pour
 * la liste et l'agenda — la règle ne se copie pas.</p>
 */
@Injectable({ providedIn: 'root' })
export class InspectionRulesService {
  private readonly now = signal(Date.now());

  constructor() {
    // Hors de la zone : un minuteur de soixante secondes tiendrait sinon
    // l'application pour « jamais stable » (whenStable, hydratation) sans rien
    // apporter. Le tic lui-même revient dans la zone, pour que « terminée »
    // se repeigne.
    const zone = inject(NgZone);
    const timer = zone.runOutsideAngular(() =>
      setInterval(() => zone.run(() => this.now.set(Date.now())), 60_000));
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  /** Une séance sans lien ni enregistrement ne s'inspecte pas encore. */
  inspectable(s: InspectableSession): boolean {
    return s.hasMeeting || s.recordingCount > 0;
  }

  /** L'heure de fin est passée. */
  ended(s: InspectableSession): boolean {
    const end = new Date(`${(s.scheduledDate ?? '').slice(0, 10)}T${s.endTime}`);
    return end.getTime() < this.now();
  }

  /**
   * Peut-on encore rejoindre la réunion ? Une réunion terminée n'est plus
   * joignable : le lien a expiré chez le fournisseur, et y entrer mène à une
   * page d'erreur. L'enregistrement prend le relais.
   */
  canJoin(s: InspectableSession): boolean {
    return s.hasMeeting && !this.ended(s);
  }

  /** Enregistrement à consulter : le plus récent non archivé, sinon le premier. */
  playable(s: InspectableSession): AppointmentRecording | null {
    const list = s.recordings ?? [];
    return list.find((r) => !r.isArchived && (r.previewUrl || r.externalUrl))
        ?? list.find((r) => r.previewUrl || r.externalUrl)
        ?? null;
  }
}
